import React, { useState } from "react";
import { Create } from "@refinedev/antd";
import { useCustomMutation, useList } from "@refinedev/core";
import { message, Form, Input, InputNumber, Select, Card, Row, Col, Descriptions, Alert, Space, Button } from "antd";
import { INVOICE_QUERY } from "../invoice/queries";
import { Invoice, InvoiceStatus } from "../invoice/interface";
import { PaymentRequestPriority, Currency, Attachment } from "./interface";
import {
  CREATE_PAYMENT_REQUEST_MUTATION,
  SUBMIT_PAYMENT_REQUEST_MUTATION,
} from "./queries";

export const CreatePaymentRequest: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Fetch approved invoices for selection
  const { data: invoicesData, isLoading: invoicesLoading } = useList<Invoice>({
    resource: "invoices",
    meta: {
      gqlQuery: INVOICE_QUERY,
    },
    pagination: {
      pageSize: 100,
    },
  });

  const invoices = (invoicesData?.data || []).filter(
    (inv) => inv.status === InvoiceStatus.APPROVED
  );

  const { mutate: createMutate, isLoading: creating } = useCustomMutation();
  const { mutate: submitMutate, isLoading: submitting } = useCustomMutation();

  const handleInvoiceChange = (invoiceId: string) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      // Auto-populate form fields from invoice
      form.setFieldsValue({
        amount: invoice.totalAmount,
        currency: invoice.currency,
        exchangeRate: 1,
      });
    }
  };

  const handleFileUpload = async (file: File) => {
    // This is a placeholder for file upload functionality
    // In a real implementation, you would upload the file to the file storage service
    // and get back a fileId
    const newAttachment: Attachment = {
      fileId: `temp-${Date.now()}`,
      originalName: file.name,
      mimeType: file.type,
      size: file.size,
      category: "document",
    };
    setAttachments([...attachments, newAttachment]);
    return false; // Prevent default upload behavior
  };

  const handleRemoveAttachment = (fileId: string) => {
    setAttachments(attachments.filter((att) => att.fileId !== fileId));
  };

  const onSaveDraft = async (values: any) => {
    if (!selectedInvoice) {
      message.error("Please select an invoice");
      return;
    }

    createMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            ...values,
            invoiceId: selectedInvoice.id,
            attachments,
          },
        },
        meta: { gqlMutation: CREATE_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request saved as draft");
          window.location.href = "/payment-requests";
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to save payment request");
        },
      },
    );
  };

  const onSubmit = async (values: any) => {
    if (!selectedInvoice) {
      message.error("Please select an invoice");
      return;
    }

    submitMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestData: {
              ...values,
              invoiceId: selectedInvoice.id,
              attachments,
            },
          },
        },
        meta: { gqlMutation: SUBMIT_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request submitted successfully");
          window.location.href = "/payment-requests";
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to submit payment request");
        },
      },
    );
  };

  return (
    <Create
      title="Create Payment Request"
      saveButtonProps={{ loading: creating, style: { display: "none" } }}
      footerButtons={() => null}
    >
      <Form form={form} layout="vertical" onFinish={onSaveDraft}>
        <Card title="Invoice Selection" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Select Invoice"
            name="invoiceId"
            rules={[{ required: true, message: "Please select an invoice" }]}
          >
            <Select
              placeholder="Select an approved invoice"
              loading={invoicesLoading}
              onChange={handleInvoiceChange}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {invoices.map((invoice) => (
                <Select.Option
                  key={invoice.id}
                  value={invoice.id}
                  label={`${invoice.invoiceNumber} - ${invoice.supplierName} - ${invoice.currency} ${invoice.totalAmount}`}
                >
                  {invoice.invoiceNumber} - {invoice.supplierName} - {invoice.currency} {invoice.totalAmount}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedInvoice && (
            <Alert
              message="Invoice Details"
              description={
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Invoice Number">{selectedInvoice.invoiceNumber}</Descriptions.Item>
                  <Descriptions.Item label="PO Number">{selectedInvoice.poNumber}</Descriptions.Item>
                  <Descriptions.Item label="Supplier">{selectedInvoice.supplierName}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">
                    {selectedInvoice.currency} {selectedInvoice.totalAmount}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">{selectedInvoice.status}</Descriptions.Item>
                  <Descriptions.Item label="Delivery References">
                    {selectedInvoice.deliveryRefs?.join(", ") || "-"}
                  </Descriptions.Item>
                </Descriptions>
              }
              type="info"
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        <Card title="Payment Request Details" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true, message: "Amount is required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder="Enter amount"
                  disabled={!!selectedInvoice}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: "Currency is required" }]}
              >
                <Select placeholder="Select currency" disabled={!!selectedInvoice}>
                  {Object.values(Currency).map((currency) => (
                    <Select.Option key={currency} value={currency}>
                      {currency}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Exchange Rate"
                name="exchangeRate"
                rules={[{ required: true, message: "Exchange rate is required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder="Enter exchange rate"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Priority"
                name="priority"
                rules={[{ required: true, message: "Priority is required" }]}
                initialValue={PaymentRequestPriority.MEDIUM}
              >
                <Select placeholder="Select priority">
                  {Object.values(PaymentRequestPriority).map((priority) => (
                    <Select.Option key={priority} value={priority}>
                      {priority}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Bank Name"
            name={["bankAccountDetails", "bank"]}
            rules={[{ required: true, message: "Bank name is required" }]}
          >
            <Input placeholder="Enter bank name" />
          </Form.Item>

          <Form.Item
            label="Account Number"
            name={["bankAccountDetails", "accountNo"]}
            rules={[{ required: true, message: "Account number is required" }]}
          >
            <Input placeholder="Enter account number" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Optional notes" />
          </Form.Item>
        </Card>

        <Card title="Attachments" style={{ marginBottom: 16 }}>
          <Form.Item label="Upload Documents">
            <Space direction="vertical" style={{ width: "100%" }}>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach(handleFileUpload);
                }}
                style={{ display: "none" }}
                id="file-upload"
              />
              <Button onClick={() => document.getElementById("file-upload")?.click()}>
                Upload Files
              </Button>
              {attachments.length > 0 && (
                <div>
                  {attachments.map((att) => (
                    <div key={att.fileId} style={{ marginBottom: 8 }}>
                      <Space>
                        <span>{att.originalName}</span>
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => handleRemoveAttachment(att.fileId)}
                        >
                          Remove
                        </Button>
                      </Space>
                    </div>
                  ))}
                </div>
              )}
            </Space>
          </Form.Item>
        </Card>

        <Card>
          <Space>
            <Button type="default" onClick={() => window.location.href = "/payment-requests"}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={creating}>
              Save as Draft
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={submitting}>
              Submit
            </Button>
          </Space>
        </Card>
      </Form>
    </Create>
  );
};
